import { MONEY_SCALE, compare, fromString, isNegative, isPositive, round, subtract, toString, zero, } from '@aeris/money';
/**
 * Financeiro — contas a pagar e a receber (Master Specification section 20).
 *
 * The module answers one question the rest of the system cannot: **who owes
 * whom, how much, and by when.** Everything before it recorded money that had
 * already moved. A sale settled at the counter and a purchase received both end
 * with the transaction closed; neither can express "the supplier is owed
 * R$ 4.320,00 in three instalments, the first on the 15th".
 *
 * ## Two tables, and why not one
 *
 *   * `finance_entry` — a **título**. One obligation with one due date and one
 *     amount. An agreement in three instalments is three rows, not one row with
 *     a count, because each instalment is separately due, separately paid,
 *     separately overdue, and separately negotiable. A single row with
 *     `instalments = 3` cannot say that the second was paid late and the third
 *     has not been issued yet.
 *
 *   * `finance_settlement` — a **baixa**. Append-only, like the stock ledger
 *     and the payment ledger before it: a settlement that can be edited is a
 *     payment that can be made to have never happened. A mistaken baixa is
 *     reversed by a new row that names it (`reversesSettlementId`), never by an
 *     update or a delete.
 *
 * ## Why this is not the `payment` table
 *
 * It was the first thing I tried. It does not fit, and the schema says so:
 * migration 0012 constrains `direction = 'out'` to mean `refunds_payment_id is
 * not null` — in the payments ledger, money leaving is a refund, by definition.
 * Paying a supplier is not a refund of anything. Widening that constraint would
 * turn the till's ledger into a general disbursement journal and cost it the
 * property that makes it auditable: that every row out reverses a row in.
 *
 * The two ledgers stay separate and meet at the `payment_method` catalogue,
 * which is where they genuinely agree — the shop pays its suppliers by the same
 * PIX it takes money with.
 *
 * ## Direction
 *
 * `receivable` and `payable` rather than a signed amount, for the reason the
 * stock ledger gives: one column holding both meanings makes every `sum()` a
 * net figure that reads like a total. What the shop is owed and what the shop
 * owes are never added together by accident here.
 *
 * Every amount is a `Decimal` from `@aeris/money`. Never a JavaScript number,
 * at any layer, including the JSON the API returns. ADR-0016.
 */
// ---------------------------------------------------------------------------
// Direction
// ---------------------------------------------------------------------------
export const FINANCE_DIRECTIONS = ['receivable', 'payable'];
export const isFinanceDirection = (value) => typeof value === 'string' &&
    FINANCE_DIRECTIONS.includes(value);
// ---------------------------------------------------------------------------
// Origin
// ---------------------------------------------------------------------------
/**
 * What the title came from.
 *
 * A **loose** reference — `originType` plus `originId`, no foreign key — for
 * exactly the reason ADR-0018 gives for payments: a title arises from a sale
 * today, a purchase today, an imported NF-e today, and from a loan, a lease or
 * a tax assessment later. A foreign key per origin is a nullable column per
 * origin plus a check constraint nobody maintains.
 *
 * With no foreign key the check constraint in migration 0019 is the only thing
 * between a typo and an orphaned title, so this list is load-bearing and adding
 * to it is a migration.
 */
export const FINANCE_ORIGIN_TYPES = [
    'sale',
    'purchase',
    /** Typed by a person: rent, energy, the accountant's fee. */
    'manual',
    'payroll',
    'tax',
    /** A diferença que o cliente deve numa troca (F126). O caixa baixa. */
    'exchange',
    /**
     * As parcelas novas de uma renegociação (F135). `origin_id` aponta a
     * renegociação, que guarda quais títulos ela substituiu.
     */
    'renegotiation',
    'commission',
];
export const isFinanceOriginType = (value) => typeof value === 'string' &&
    FINANCE_ORIGIN_TYPES.includes(value);
// ---------------------------------------------------------------------------
// What kind of arrangement the title is
// ---------------------------------------------------------------------------
/**
 * Whether the due date is an arrangement or a pending settlement (F159).
 *
 * Two titles that look identical in the database — receivable, open, raised by a
 * sales document — can mean opposite things:
 *
 *   - `terms` — somebody agreed to pay later. The counter's crediário, the
 *     difference a customer owes on an exchange, a title typed by hand. The
 *     business is closed; only the money is scheduled.
 *   - `on-delivery` — the goods went out and the money is waiting for someone to
 *     come back and account for it. The courier is on the street. **The business
 *     is not finished**, and until this title closes there is nothing to exchange.
 *
 * The owner's rule is built on exactly that distinction: a delivered order can be
 * exchanged once it has been settled, and a sale on terms can be exchanged while
 * its instalments are still open — the return then reduces them.
 *
 * It is a column and not a derivation because the alternative was
 * `expectedMethodId`, which migration 0019 documents as advisory and which
 * `update` lets anyone change. A rule about who may exchange cannot rest on a
 * field that exists to hold a guess.
 */
export const FINANCE_DUE_BASES = ['terms', 'on-delivery'];
export const isFinanceDueBasis = (value) => typeof value === 'string' &&
    FINANCE_DUE_BASES.includes(value);
// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------
/**
 * `open` while anything is outstanding, `settled` when nothing is, `cancelled`
 * when the obligation was withdrawn.
 *
 * Note what is absent: **`overdue` is not a status.** It is a fact about today
 * and a due date, and a row that carried it would be wrong every midnight until
 * something rewrote it — which is a nightly job whose failure mode is a screen
 * that quietly under-reports what the shop is owed. `isOverdue()` derives it,
 * and it derives it from the same clock the screen is drawn with.
 */
export const FINANCE_STATUSES = ['open', 'settled', 'cancelled'];
export const isFinanceStatus = (value) => typeof value === 'string' &&
    FINANCE_STATUSES.includes(value);
// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------
/** R$ 0,00, at the scale every column here carries. */
export const ZERO_AMOUNT = zero(MONEY_SCALE);
/**
 * Reads an amount from a string.
 *
 * A string, always: `numeric` arrives from the pg driver as text and travels
 * the wire as text, precisely so no boundary can round it.
 */
export const parseAmount = (value) => round(fromString(value.trim()), MONEY_SCALE, 'half-up');
/** The wire and database format. Never a JSON number. */
export const amountToString = (value) => toString(value);
// ---------------------------------------------------------------------------
// Derived facts
// ---------------------------------------------------------------------------
/** What is still owed on this title. Zero once it is settled. */
export const outstanding = (entry) => {
    const rest = subtract(entry.amount, entry.settledAmount);
    return isNegative(rest) ? ZERO_AMOUNT : rest;
};
/**
 * Whether the title is past its due date **as of `now`**.
 *
 * Takes the clock as an argument rather than calling it. A function that reads
 * the wall clock cannot be tested at a boundary, and every interesting case
 * here is a boundary: the last second of the due date, and the first of the
 * day after.
 */
export const isOverdue = (entry, now) => entry.status === 'open' &&
    isPositive(outstanding(entry)) &&
    entry.dueAt.getTime() < now.getTime();
/**
 * Whole days a title is late. Zero when it is not.
 *
 * Calendar days rather than elapsed 24-hour periods: a boleto due yesterday is
 * one day late at nine this morning, which is what a shopkeeper means and what
 * every juros-per-day arrangement is written against.
 */
export const daysOverdue = (entry, now) => {
    const startOfDay = (moment) => Date.UTC(moment.getFullYear(), moment.getMonth(), moment.getDate());
    const days = Math.floor((startOfDay(now) - startOfDay(entry.dueAt)) / 86_400_000);
    return days > 0 ? days : 0;
};
/**
 * Whether a status change is the one the amounts imply.
 *
 * The repository calls this after moving `settledAmount`, so that "settled" is
 * a consequence of the arithmetic rather than a second decision somebody could
 * make differently.
 */
export const statusAfterSettlement = (entry) => {
    if (entry.status === 'cancelled')
        return 'cancelled';
    return compare(entry.settledAmount, entry.amount) >= 0 ? 'settled' : 'open';
};
//# sourceMappingURL=entities.js.map